// Reference images (2B): everything that happens to a picked, pasted or
// dropped file before it leaves the browser. Every image is decoded, fitted
// inside MAX_IMAGE_EDGE and re-encoded as JPEG (react-pdf can't reliably
// decode WebP), aiming for TARGET_IMAGE_BYTES and never over MAX_IMAGE_BYTES.
//
// HEIC (iPhone photos) uses the browser's own decoder: Safari on Mac and
// iPhone decodes it, and the iPhone photo picker hands over a JPEG anyway.
// Where the browser can't (Chrome on a Mac, for a file dragged from Finder),
// the message says how to get a usable copy. No bundled HEIC decoder: see
// docs/gates/GATE-FB-2B.md.

import {
  MAX_IMAGE_BYTES,
  MAX_IMAGE_EDGE,
  STORED_IMAGE_MIME,
  TARGET_IMAGE_BYTES,
} from '../../shared/refImages';

export const IMAGE_TYPES_HINT = 'Use a JPG, PNG, WebP or iPhone photo (HEIC).';

const DIRECT_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);
const HEIC_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
const DIRECT_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp']);
const HEIC_EXTENSIONS = new Set(['heic', 'heif']);

function extensionOf(name: string): string {
  const match = /\.([a-z0-9]+)$/i.exec(name);
  return match ? match[1]!.toLowerCase() : '';
}

export type FileCheck = { ok: true; heic: boolean } | { ok: false; message: string };

/** Accepts JPG, PNG, WebP and HEIC by type or, when the browser leaves the
 *  type blank (HEIC on most desktops), by extension. */
export function checkImageFile(file: { name: string; type: string }): FileCheck {
  const type = file.type.toLowerCase();
  const ext = extensionOf(file.name);
  if (HEIC_TYPES.has(type) || HEIC_EXTENSIONS.has(ext)) {
    return { ok: true, heic: true };
  }
  if (DIRECT_TYPES.has(type) || (!type && DIRECT_EXTENSIONS.has(ext))) {
    return { ok: true, heic: false };
  }
  const label = ext ? ext.toUpperCase() : '';
  return {
    ok: false,
    message: label
      ? `That's a ${label}. ${IMAGE_TYPES_HINT}`
      : `That file isn't a photo we can use. ${IMAGE_TYPES_HINT}`,
  };
}

/** Width and height fitted inside a MAX_IMAGE_EDGE square, never upscaled. */
export function fitWithin(width: number, height: number, edge = MAX_IMAGE_EDGE): { width: number; height: number } {
  const scale = Math.min(1, edge / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Qualities tried in turn until the JPEG lands under the target. */
export const JPEG_QUALITIES = [0.82, 0.74, 0.66, 0.58];

export interface ProcessedImage {
  blob: Blob;
  width: number;
  height: number;
}

export class ImageProcessError extends Error {}

export const HEIC_UNSUPPORTED_MESSAGE =
  "This browser can't open iPhone photos (HEIC). Open the page in Safari, or export the photo as a JPG.";

async function decode(file: File): Promise<ImageBitmap> {
  // `from-image` applies the photo's EXIF rotation, so portraits stay upright.
  return createImageBitmap(file, { imageOrientation: 'from-image' });
}

function toJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, STORED_IMAGE_MIME, quality));
}

export async function processImageFile(file: File, heic: boolean): Promise<ProcessedImage> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await decode(file);
  } catch {
    throw new ImageProcessError(
      heic ? HEIC_UNSUPPORTED_MESSAGE : `Couldn't read ${file.name || 'that image'}. ${IMAGE_TYPES_HINT}`,
    );
  }
  const { width, height } = fitWithin(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageProcessError(`Couldn't read ${file.name || 'that image'}.`);
  // JPEG has no transparency: a logo with a clear background lands on white.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  let best: Blob | null = null;
  for (const quality of JPEG_QUALITIES) {
    const blob = await toJpeg(canvas, quality);
    if (!blob) continue;
    best = blob;
    if (blob.size <= TARGET_IMAGE_BYTES) break;
  }
  if (!best) throw new ImageProcessError(`Couldn't read ${file.name || 'that image'}.`);
  if (best.size > MAX_IMAGE_BYTES) {
    throw new ImageProcessError('That image is over 2 MB even after resizing. Try a smaller photo.');
  }
  return { blob: best, width, height };
}
